import React, {
  ReactNode,
  Component,
  ErrorInfo,
  Context,
  ComponentType,
  PropsWithChildren,
} from 'react';

import { Flex, Header, rem } from '@vimeo/bokeh';
import { IconButton } from '@vimeo/bokeh';
import { Refresh } from '@vimeo/bokeh-icons';
import { AnyObject, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';
import { Viewer, ViewerContext } from '@vimeo/viewer';

import { environmentConfig } from '../../../configs/EnvironmentConfig';
import { T_ERROR_SOMETHING_UNEXPECTED } from '../../../translations';
import { createDomName } from '../../../utils/dom/identifying';
import { ELiveErrorCategory, trackLiveError } from '../../../utils/tracking';
import { isStaffUser } from '../../../utils/vimeo/user';
import { JSONTable } from '../../json';

interface ILiveErrorBoundaryProps {
  message?: string;
  component?: string;
  componentHandler?: ComponentType<{ error: Error }>;
  isDetailed?: boolean;
  isResetEnabled?: boolean;
  additionalInfo?: unknown;
}

interface ILiveErrorBoundaryState {
  error: Optional<Error>;
}

/**
 * Live error boundary.
 * Prevent page crashing on issues with UI rendering.
 *
 * If has handler component, just provides error for it and renders.
 * If has no handler component, uses default layout for smaller parts.
 */
export class LiveErrorBoundary extends Component<
  ILiveErrorBoundaryProps & PropsWithChildren,
  ILiveErrorBoundaryState,
  Viewer
> {
  public static override contextType: Context<Optional<Viewer>> = ViewerContext;

  public override state: ILiveErrorBoundaryState = {
    error: null,
  };

  private isTracked: boolean = false;
  private log: Logger = new Logger('🐈‍⬛EB');

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.log.error('Rendering failed:', error, errorInfo, this.props.component);
    this.setState({ error });

    /**
     * Track errors only first time to prevent 'refresh' attempts spamming in NR logs.
     */
    if (!this.isTracked) {
      trackLiveError(error, {
        component: this.props.component || 'LiveErrorBoundary',
        category: ELiveErrorCategory.GENERIC,
        data: { errorInfo, additionalInfo: this.props.additionalInfo },
      });
    }
  }

  public onTryResetError = (): void => {
    this.log.info('Resetting error:', this.state.error);
    this.setState({ error: null });
  };

  public override render(): ReactNode {
    const {
      isDetailed = true,
      isResetEnabled = true,
      componentHandler: Handler,
      message,
    } = this.props;
    const { error } = this.state;

    if (error && Handler) {
      return <Handler error={error} />;
    } else if (error) {
      const errorData: AnyObject = { ...error, message: error?.message, name: error?.name };
      const isDevErrorShown: boolean = Boolean(
        isDetailed &&
          (environmentConfig.IS_DEV ||
            environmentConfig.IS_CI ||
            // @ts-expect-error class does not have a typed context
            isStaffUser(this.context?.user)) &&
          error,
      );

      return (
        <Flex
          id={createDomName('error-boundary')}
          className={createDomName('error-boundary')}
          direction={'column'}
          grow={1}
          position={'relative'}
          width={'100%'}
          height={'100%'}
        >
          <Flex
            direction={'column'}
            position={'absolute'}
            padding={rem(20)}
            textAlign={'center'}
            color={'text-primary'}
            width={'100%'}
            height={'100%'}
          >
            <Flex
              id={createDomName('error-boundary-title')}
              className={createDomName('error-boundary-title')}
              alignItems={'center'}
              justifyContent={'center'}
            >
              {message ?? T_ERROR_SOMETHING_UNEXPECTED}
              &nbsp; &nbsp;
              {isResetEnabled ? (
                <IconButton
                  aria-label={'reset'}
                  size={'xs'}
                  icon={<Refresh />}
                  onClick={this.onTryResetError}
                />
              ) : null}
            </Flex>
            <br />
            {isDevErrorShown ? (
              <JSONTable
                id={createDomName('error-boundary-details')}
                className={createDomName('error-boundary-details')}
                header={<Header size={'sm'}> Details (staff only): </Header>}
                data={errorData}
              />
            ) : null}
          </Flex>
        </Flex>
      );
    }

    return this.props.children;
  }
}
